import { Loader2 } from "lucide-react";
import { formatBytes, type PickedZip } from "@/lib/dataTransfer";
import { BackupSummaryView } from "./BackupSummaryView";

/**
 * O estado do ZIP escolhido no primeiro passo da importação: o andamento do
 * envio, a conferência no servidor, o erro ou, conferido, o resumo com os
 * avisos (outro projeto, troca do bucket nas URLs, ZIP vazio).
 */
export function ImportFileStatus({
  picked,
  otherProject,
  currentProjectId,
  empty,
}: {
  picked: PickedZip;
  /** O projeto do ZIP, quando não é o do painel. */
  otherProject: string | null;
  currentProjectId: string;
  empty: boolean;
}) {
  if (picked.state === "uploading") {
    const percent = Math.round(picked.fraction * 100);
    return (
      <div className="space-y-1.5">
        <p className="flex justify-between gap-2 text-caption">
          <span className="truncate">Enviando {picked.name}</span>
          <span className="shrink-0 tabular-nums text-muted-foreground">
            {formatBytes(picked.size * picked.fraction)} de {formatBytes(picked.size)}
          </span>
        </p>
        {/* A trilha usa a cor da borda: a de `muted` já vem com transparência
            na variável e some com o modificador do Tailwind (ver docs/10). */}
        <div
          role="progressbar"
          aria-label="Envio do arquivo"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
          className="h-2 overflow-hidden rounded-full bg-border"
        >
          <div
            className="h-full rounded-full bg-primary transition-[width]"
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>
    );
  }

  if (picked.state === "inspecting") {
    return (
      <p className="flex items-center gap-2 text-caption text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> O servidor está lendo e conferindo{" "}
        {picked.name}, sem gravar nada.
      </p>
    );
  }

  if (picked.state === "failed") {
    return (
      <p className="break-words text-small text-destructive">
        {picked.name}: {picked.error}
      </p>
    );
  }

  const { inspection } = picked;
  return (
    <BackupSummaryView summary={inspection.summary} fileName={picked.name} zipBytes={inspection.zipBytes}>
      {otherProject ? (
        <p className="mt-2 text-caption font-semibold text-state-warning">
          Este ZIP é do projeto {otherProject}, e o painel está no {currentProjectId}.
        </p>
      ) : null}
      {inspection.rewrittenBucket ? (
        <p className="mt-2 break-words text-caption text-muted-foreground">
          As URLs dos arquivos gravadas no Firestore apontam para o bucket{" "}
          {inspection.rewrittenBucket.from}. Na importação, elas passam a apontar para o{" "}
          {inspection.rewrittenBucket.to}, com o mesmo caminho e o mesmo token.
        </p>
      ) : null}
      {empty ? (
        <p className="mt-2 text-caption font-semibold text-destructive">
          O ZIP não tem documento nem arquivo: importá-lo só apagaria tudo.
        </p>
      ) : null}
    </BackupSummaryView>
  );
}
